import {
  getAccessKeyForRoute,
  hasAccess as hasAccessFromPlan,
  hasFeature as hasFeatureFromPlan,
  normalizePlan,
} from "../accessControl";
import {
  canAccessPkgRoute,
  packageAccess,
  getUserPlanAccess,
  getEffectivePlanId,
  getEffectivePlanType,
  isPlanAccessHydrating,
  isSubscriptionAccessHydrating,
  isProfileSubscriptionActive,
  isFreemiumBillingState,
} from "../helpers";
import {
  SOFT_UPGRADE_GATED_PKG_ACCESS,
  BILLING_RESTRICTED_ALLOWED_PATHS,
  ACCESS_REASON,
} from "./constants";

/**
 * Normalize route/pkg keys (fixes "Dashboard" vs "DASHBOARD" mismatches).
 * @param {string|null|undefined} key
 * @returns {string|null}
 */
export function normalizePkgAccessKey(key) {
  if (key == null || key === "") return null;
  return String(key).trim().toUpperCase();
}

/**
 * @param {object} userProfile
 * @param {{ isSuperUser?: boolean }} [options]
 */
export function isAccessReady(userProfile, options = {}) {
  const { isSuperUser = false } = options;
  if (isSuperUser) return true;
  if (isSubscriptionAccessHydrating(userProfile, { isSuperUser })) return false;
  if (isPlanAccessHydrating(userProfile, "DASHBOARD", { isSuperUser })) return false;
  return true;
}

/**
 * Synchronous resolved permissions: Firestore plan doc when present, else Stripe tier static map.
 * @param {object} userProfile
 * @returns {import('../helpers').packageAccess extends Function ? ReturnType<typeof packageAccess> : object}
 */
export function getResolvedPermissions(userProfile = {}) {
  const planId = getEffectivePlanId(userProfile);
  const planAccess = getUserPlanAccess(userProfile);
  const profileOrPlanId = userProfile?.prdId ? userProfile : planId;
  return packageAccess(profileOrPlanId, null, null, planAccess);
}

/**
 * Whether a pkg route key is included in resolved plan permissions (DB-first, static fallback).
 * @param {object} userProfile
 * @param {string} pkgAccessKey
 */
export function isPkgRouteAllowed(userProfile = {}, pkgAccessKey) {
  const normalized = normalizePkgAccessKey(pkgAccessKey);
  if (!normalized) return true;

  if (normalized === PROOF_OF_SERVICE_REPORT_KEY) {
    if (!canAccessProofOfServiceReport(userProfile)) return false;
    if (canAccessPkgRoute(userProfile, normalized)) return true;
    const accessKey = getAccessKeyForRoute(normalized);
    if (accessKey && canAccessModule(userProfile, accessKey)) return true;
    return true;
  }

  if (canAccessPkgRoute(userProfile, normalized)) return true;

  // Freemium has no planAccess doc; module fallback would recurse via moduleAllowedByRoutes.
  if (isFreemiumBillingState(userProfile)) return false;

  const accessKey = getAccessKeyForRoute(normalized);
  if (accessKey && canAccessModule(userProfile, accessKey)) return true;

  return false;
}

const MODULE_ROUTE_KEYS = Object.freeze({
  supplies: ["SUPPLIES", "MANAGE_SUPPLIES", "ARCHIVE_SUPPLIES"],
  issues: ["ISSUES", "MANAGE_ISSUES", "ARCHIVE_ISSUES"],
  messaging: ["MESSAGE_BOARD", "LIVE_CHAT", "CREATE_CHANNEL", "MANAGE_CHANNEL"],
  notifications: ["NOTIFICATION", "CREATE_NOTIFICATION", "MANAGE_NOTIFICATION"],
  reports_standard: ["STANDARD_REPORTS", "REPORTS"],
  reports_advanced: ["ADVANCED_REPORTS"],
  proof_of_service: ["PROOF_OF_SERVICE", "POS_WORK", "PROOF_OF_SERVICE_REPORT"],
  scheduling: ["APPOINTMENTS", "MANAGE_APPOINTMENT", "CREATE_APPOINTMENT"],
  billing: ["BILLING", "UPGRADE"],
});

const PROOF_OF_SERVICE_REPORT_KEY = "PROOF_OF_SERVICE_REPORT";

/**
 * Proof of Service report: freemium only; all other active paid tiers.
 * @param {object} userProfile
 */
function canAccessProofOfServiceReport(userProfile = {}) {
  if (isFreemiumBillingState(userProfile)) return false;
  if (getEffectivePlanType(userProfile) === "freemium") return false;
  return Boolean(getEffectivePlanId(userProfile));
}

/** Feature paths that gate UI but map to a portal module route. */
/** When billing is freemium, ignore stale cached planAccess (matches packageAccess). */
function getPlanAccessDocForChecks(userProfile = {}) {
  if (isFreemiumBillingState(userProfile)) return null;
  return getUserPlanAccess(userProfile);
}

const FEATURE_ROUTE_FALLBACK = Object.freeze({
  "supply_manager.requests": "supplies",
  "supply_manager.archive": "supplies",
  "supply_manager.predefined_list": "supplies",
  "supply_manager.custom_supplies": "supplies",
  "issue_manager.create": "issues",
  "issue_manager.archive_duration": "issues",
  "issue_manager.internal_escalation": "issues",
  "issue_manager.client_escalation": "issues",
  "core_operations.issues": "issues",
  "core_operations.supplies": "supplies",
});

function moduleAllowedByRoutes(userProfile = {}, accessKey) {
  const moduleKey = String(accessKey || "").trim().toLowerCase();
  const routeKeys = MODULE_ROUTE_KEYS[moduleKey] || [];
  // Use canAccessPkgRoute directly — isPkgRouteAllowed would call back into canAccessModule.
  return routeKeys.some((routeKey) => canAccessPkgRoute(userProfile, routeKey));
}

/**
 * Module access: Firestore plan doc first, static tier only when no doc on profile.
 * @param {object} userProfile
 * @param {string} accessKey — webPortalAccess module key (e.g. "supplies")
 */
export function canAccessModule(userProfile = {}, accessKey) {
  if (!accessKey) return true;
  const planAccess = getPlanAccessDocForChecks(userProfile);
  if (planAccess) {
    return hasAccessFromPlan(planAccess, accessKey);
  }
  return moduleAllowedByRoutes(userProfile, accessKey);
}

/**
 * @param {object} userProfile
 * @param {string} featurePath — dot path under plan.features
 */
export function canAccessFeature(userProfile = {}, featurePath) {
  if (!featurePath) return true;

  const planAccess = getPlanAccessDocForChecks(userProfile);
  if (planAccess) {
    if (hasFeatureFromPlan(planAccess, featurePath)) return true;
    const moduleKey = FEATURE_ROUTE_FALLBACK[featurePath];
    if (moduleKey && hasAccessFromPlan(planAccess, moduleKey)) return true;
    if (String(featurePath).startsWith("issue_manager.")) {
      return hasAccessFromPlan(planAccess, "issues");
    }
    if (String(featurePath).startsWith("supply_manager.")) {
      return hasAccessFromPlan(planAccess, "supplies");
    }
    return false;
  }

  const moduleKey = FEATURE_ROUTE_FALLBACK[featurePath];
  if (moduleKey && moduleAllowedByRoutes(userProfile, moduleKey)) return true;
  if (String(featurePath).startsWith("issue_manager.")) {
    return moduleAllowedByRoutes(userProfile, "issues");
  }
  if (String(featurePath).startsWith("supply_manager.")) {
    return moduleAllowedByRoutes(userProfile, "supplies");
  }
  return false;
}

/**
 * Central route access decision for secure routes and sidebar.
 * @param {object} userProfile
 * @param {string|null|undefined} pkgAccessKey
 * @param {object} [options]
 * @param {boolean} [options.isSuperUser]
 * @param {boolean} [options.allowSoftGatedSupplies]
 * @param {boolean} [options.billingAccessRestricted]
 * @param {boolean} [options.subscriptionExpired] — true when substatus invalid
 * @param {string} [options.routePath]
 */
export function evaluateRouteAccess(userProfile = {}, pkgAccessKey, options = {}) {
  const {
    isSuperUser = false,
    allowSoftGatedSupplies = false,
    billingAccessRestricted = false,
    routePath = null,
  } = options;

  if (!pkgAccessKey) {
    return { ready: true, allowed: true, reason: ACCESS_REASON.NO_PKG_KEY };
  }

  if (isSuperUser) {
    return { ready: true, allowed: true, reason: ACCESS_REASON.SUPERUSER };
  }

  const subscriptionHydrating = isSubscriptionAccessHydrating(userProfile, {
    isSuperUser,
  });
  const planHydrating = isPlanAccessHydrating(userProfile, pkgAccessKey, {
    isSuperUser,
  });
  const ready = !subscriptionHydrating && !planHydrating;

  if (!ready) {
    return { ready: false, allowed: true, reason: ACCESS_REASON.LOADING };
  }

  if (
    billingAccessRestricted &&
    routePath &&
    !BILLING_RESTRICTED_ALLOWED_PATHS.includes(routePath)
  ) {
    return {
      ready: true,
      allowed: false,
      reason: ACCESS_REASON.BILLING_RESTRICTED,
    };
  }

  const normalized = normalizePkgAccessKey(pkgAccessKey);
  if (
    allowSoftGatedSupplies &&
    SOFT_UPGRADE_GATED_PKG_ACCESS.includes(normalized)
  ) {
    return { ready: true, allowed: true, reason: ACCESS_REASON.SOFT_GATE };
  }

  const allowed = isPkgRouteAllowed(userProfile, normalized);
  return {
    ready: true,
    allowed,
    reason: allowed ? ACCESS_REASON.PLAN_ROUTE : ACCESS_REASON.PLAN_DENIED,
    accessKey: getAccessKeyForRoute(normalized),
  };
}

/**
 * @param {object} userProfile
 * @param {{ isSuperUser?: boolean }} [options]
 */
export function isSubscriptionAccessDenied(userProfile = {}, options = {}) {
  const { isSuperUser = false } = options;
  if (isSuperUser) return false;
  if (!isAccessReady(userProfile, { isSuperUser })) return false;
  return !isProfileSubscriptionActive(userProfile);
}

/**
 * @param {object|null|undefined} planDoc
 */
export function normalizePlanDocument(planDoc) {
  return planDoc ? normalizePlan(planDoc) : null;
}

/**
 * @param {object} userProfile
 * @param {string|null|undefined} pkgAccessKey
 * @param {object} [options]
 */
export function canAccessPkgRouteFromContext(userProfile = {}, pkgAccessKey, options = {}) {
  const result = evaluateRouteAccess(userProfile, pkgAccessKey, options);
  if (!result.ready) return true;
  return result.allowed;
}
